"use client";

import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import useDebounce from '@/hooks/use-debounce';
import { getBrands, getModels } from '@/lib/supabase/brands';
import { Loader2 } from '@/components/icons';

interface ModelAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  /** optional: called when user selects a suggestion; provides brand+model */
  onSelect?: (v: { brand?: string; model: string }) => void;
  /** optional: allow narrowing models by brand */
  brandFilter?: string | null;
  disabled?: boolean;
  placeholder?: string;
}

// Starter dataset with brand mapping. Can be extended or replaced by a server-side source later.
const COMMON_MODELS: Array<{ brand?: string; model: string; label: string }> = [
  { brand: 'Toyota', model: 'Corolla', label: 'Toyota Corolla' },
  { brand: 'Honda', model: 'Civic', label: 'Honda Civic' },
  { brand: 'Ford', model: 'Focus', label: 'Ford Focus' },
  { brand: 'Ford', model: 'Fiesta', label: 'Ford Fiesta' },
  { brand: 'Toyota', model: 'Camry', label: 'Toyota Camry' },
  { brand: 'Toyota', model: 'Hilux', label: 'Toyota Hilux' },
  { brand: 'Ford', model: 'Ranger', label: 'Ford Ranger' },
  { brand: 'Volkswagen', model: 'Gol', label: 'Volkswagen Gol' },
  { brand: 'Fiat', model: 'Palio', label: 'Fiat Palio' },
  { brand: 'Renault', model: 'Sandero', label: 'Renault Sandero' },
  { brand: 'Peugeot', model: '208', label: 'Peugeot 208' },
  { brand: 'Peugeot', model: '308', label: 'Peugeot 308' },
  { brand: 'Chevrolet', model: 'Cruze', label: 'Chevrolet Cruze' },
  { brand: 'Chevrolet', model: 'Onix', label: 'Chevrolet Onix' },
  { brand: 'Toyota', model: 'Yaris', label: 'Toyota Yaris' },
  { brand: 'Nissan', model: 'Sentra', label: 'Nissan Sentra' },
  { brand: 'Renault', model: 'Logan', label: 'Renault Logan' },
  { brand: 'Volkswagen', model: 'Vento', label: 'Volkswagen Vento' }
];

const ModelAutocomplete: React.FC<ModelAutocompleteProps> = ({ value, onChange, onSelect, brandFilter, disabled, placeholder = 'Empieza a escribir el modelo (ej. Corolla)' }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState<Array<{ brand?: string; model: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [userTyped, setUserTyped] = useState(false);
  const [offline, setOffline] = useState(false);
  const [selected, setSelected] = useState<{ brand?: string; model: string } | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const debounced = useDebounce(inputValue, 300);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // When brandFilter changes (user selected a brand), reset userTyped so suggestions don't auto-show
  useEffect(() => {
    setUserTyped(false);
    // also clear suggestions when brand changes
    setSuggestions([]);
  }, [brandFilter]);

  useEffect(() => {
    const term = debounced.trim();
    // Only show suggestions if the user has typed something (prevents auto-display on mount)
    if (!term || !userTyped) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const fetchSuggestions = async () => {
      try {
        const data = await getModels(brandFilter || null, term || null);
        setOffline(false);
        const entries = Array.isArray(data) ? data.map(d => ({ brand: d.brand, model: d.model, label: `${d.brand} ${d.model}` })) : [];
        const lower = term.toLowerCase();
        const exactMatch = entries.some(f => f.label.toLowerCase() === lower || f.model.toLowerCase() === lower);
        setSuggestions(exactMatch ? [] : entries.slice(0, 8));
        setLoading(false);
        return;
      } catch (err) {
        // mark offline so UI can show a helpful hint; fall back to local dataset
        setOffline(true);
        // fallback to client-side COMMON_MODELS if fetch fails
        const lower = term.toLowerCase();
        let candidates = COMMON_MODELS;
        if (brandFilter) {
          candidates = candidates.filter(c => String(c.brand || '').toLowerCase() === String(brandFilter).toLowerCase());
        }
        const filtered = candidates.filter(m => (term ? (m.label.toLowerCase().includes(lower) || m.model.toLowerCase().includes(lower)) : true)).slice(0, 8);
        const exactMatch = filtered.some(f => f.label.toLowerCase() === lower || f.model.toLowerCase() === lower);
        setSuggestions(exactMatch ? [] : filtered);
        setLoading(false);
      }
    };

    const t = setTimeout(() => {
      void fetchSuggestions();
    }, 150);

    return () => clearTimeout(t);
  }, [debounced, brandFilter, userTyped]);

  return (
    <div className="relative">
      <Input
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          onChange(e.target.value);
          setUserTyped(true);
          // typing invalidates prior selection
          setSelected(null);
          setValidationError('');
        }}
        onBlur={() => {
          // allow click handlers to run first
          setTimeout(() => {
            if (!selected && inputValue.trim() !== '') {
              // enforce selection-only: clear input and notify parent
              setValidationError('Debes seleccionar un modelo de la lista');
              setInputValue('');
              onChange('');
            } else {
              setValidationError('');
            }
          }, 120);
        }}
        disabled={disabled}
      />
      {validationError && (
        <div className="text-xs text-red-600 mt-1">{validationError}</div>
      )}
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primary" />}

      {suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-56 overflow-y-auto">
          {offline && (
            <div className="w-full p-2 text-xs text-foreground/80 bg-warning-muted border-b">Sin conexión — mostrando resultados locales</div>
          )}
          {suggestions.map((entry, i) => {
            const modelText = entry.model;
            const brandText = entry.brand;
            return (
              <button
                key={i}
                className="w-full text-left p-2 hover:bg-muted/30 transition-colors border-b last:border-b-0"
                onClick={() => {
                      if (onSelect) onSelect({ brand: entry.brand, model: entry.model });
                      setInputValue(entry.model);
                      onChange(entry.model);
                      setSuggestions([]);
                      setUserTyped(false);
                      setOffline(false);
                      setSelected({ brand: entry.brand, model: entry.model });
                      setValidationError('');
                    }}
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{modelText}</span>
                  {brandText && !brandFilter && (
                    <span className="text-xs text-muted-foreground">{brandText}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


// BrandAutocomplete: simple autocomplete that suggests brands found in COMMON_MODELS
interface BrandAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (brand: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const BrandAutocomplete: React.FC<BrandAutocompleteProps> = ({ value, onChange, onSelect, disabled, placeholder = 'Empieza a escribir la marca (ej. Toyota)' }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [userTyped, setUserTyped] = useState(false);
  const [offline, setOffline] = useState(false);
  const debounced = useDebounce(inputValue, 200);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const term = debounced.trim();
    // Only show suggestions if the user has typed something
    if (!term || !userTyped) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const fetchBrands = async () => {
      setLoading(true);
      try {
        const data = await getBrands(term || null);
        setOffline(false);
        const filtered = Array.isArray(data) ? data.slice(0, 8) : [];
        setSuggestions(filtered);
        setLoading(false);
        return;
      } catch (err) {
        setOffline(true);
        // fallback to client-side list
        const brands = Array.from(new Set(COMMON_MODELS.map(m => m.brand).filter(Boolean))).map(String);
        const lower = term.toLowerCase();
        const filtered = brands.filter(b => b.toLowerCase().includes(lower)).slice(0, 8);
        setSuggestions(filtered);
        setLoading(false);
      }
    };

    const t = setTimeout(() => { void fetchBrands(); }, 120);
    return () => clearTimeout(t);
  }, [debounced, userTyped]);

  return (
    <div className="relative">
      <Input
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => { setInputValue(e.target.value); onChange(e.target.value); setUserTyped(true); }}
        disabled={disabled}
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primary" />}

      {suggestions.length > 0 && (
        <div className="mt-3 space-y-2">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => {
                if (onSelect) onSelect(s);
                setInputValue(s);
                onChange(s);
                setUserTyped(false);
                setSuggestions([]);
              }}
              className="w-full text-left p-3 border rounded-lg hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">{s}</span>
                {offline && (
                  <span className="text-xs text-yellow-600">Sin conexión — local</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModelAutocomplete;
